import type { AlgoliaOffer } from '@global-types/algoliaOffer.ts';
import { OfficeStateEnum } from '@organisms/offer/office-state-enum';
import { isPriceDropRecent } from '@organisms/offer/offer-utils';
import { trans, transchoice } from '@tools/translation.ts';
import { OfficeOfferPriceType } from '@enums/officeOfferPriceType.ts';
import type { OfficeType } from '@enums/officeType.ts';

const getTypeInfos = (offer: AlgoliaOffer): string | null => {
  if (offer.is_aggregate) {
    return trans('offer_card.display_types.multioffice', { count: offer.aggregate.other_offers.length });
  }

  const officeTypeCode = offer.office_types?.[0]?.code;

  if (!offer.display_type) {
    return officeTypeCode ? trans(`offer_card.display_types.${officeTypeCode}`) : null;
  }

  const typeInfos = trans(`offer_card.display_types.${offer.display_type}`);

  if (offer.display_type === 'flex_operator') {
    return typeInfos;
  }

  return officeTypeCode
    ? `${trans(`offer_card.display_types.${officeTypeCode}`)} • ${typeInfos.toLowerCase()}`
    : typeInfos;
};

const getOfferInfos = (offer: AlgoliaOffer): string => {
  const capacityLabel = offer.capacity
    ? `${offer.capacity} ${transchoice('global.post_office', { count: offer.capacity })}`
    : '';

  const surfaceLabel = offer.surface
  && offer.price_type !== OfficeOfferPriceType.RENT_WORKSTATION
    ? `${offer.surface} ${trans('global.meter.square')}`
    : '';

  return [ capacityLabel, surfaceLabel ].filter(Boolean)
    .join(' • ');
};

const isRentSurface = (offer: AlgoliaOffer) => offer.price_type === OfficeOfferPriceType.RENT_SURFACE;

export const getTotalMonthlyPrice = (offer: AlgoliaOffer): number => {
  if (isRentSurface(offer)) {
    return Number(((offer.price * offer.surface) / 12).toFixed(2));
  }

  return offer.price;
};

export type DeserializedOffer = AlgoliaOffer & Record<string, unknown> & {
  address: AlgoliaOffer['address'] & {
    latitude: number,
    longitude: number,
  },
  is_price_drop_recent: boolean,
  availability_date: {
    iso: string,
    unix: number,
  },
  office_type: {
    code: OfficeType,
  },
  aggregated_offers: {
    hash_id: string,
    office_type: {
      code: OfficeType,
    },
    surface: number,
    capacity: number,
  }[],
  monthly_total_price: number,
};

export const getDeserializedOffer = (offer: AlgoliaOffer): DeserializedOffer => ({
  ...offer,
  name: offer.title,
  public_url: offer.uri,
  total_price: offer.price,
  monthly_total_price: getTotalMonthlyPrice(offer),
  state: OfficeStateEnum.PUBLISHED,
  type_infos: getTypeInfos(offer),
  offer_infos: getOfferInfos(offer),
  address: {
    ...offer.address,
    latitude: offer._geoloc.lat,
    longitude: offer._geoloc.lng,
  },
  is_flex_operator_display_type: offer.display_type === 'flex_operator',
  first_station_distance: {
    unique_lines: offer.address.first_station?.unique_lines ?? [],
  },
  is_accessible: true,
  availability_date: {
    iso: new Date(offer.availability_date.unix * 1000).toISOString(),
    unix: offer.availability_date.unix,
  },
  is_price_drop_recent: isPriceDropRecent(offer.price_dropped_at),
  office_type: {
    code: offer.office_types[0].code,
  },
  aggregated_offers: offer.aggregate?.other_offers.map((aggregatedOffer, index) => ({
    hash_id: offer.aggregate.hash_ids[index],
    office_type: {
      code: aggregatedOffer.type,
    },
    capacity: aggregatedOffer.teamsize,
    surface: aggregatedOffer.surface,
  })) ?? [],
});
